import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { Languages } from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { ellipsis } from "@f2e/shared/utils"

import { useLogoutApi } from "@/hooks/api/account"
import { User } from "@/types"
import { useStore } from "@/store"
import { getAvatarName, getAvatarBackgroundColor } from "@/lib/utils"
import { useResourceUrl } from "@/hooks"
import { SETTINGS_ENDPOINTS } from "@/constants"

interface UserNavProps {
  className?: string
  user: User | null
  showProfile?: boolean
  size?: "sm" | "md" | "lg"
}

const LANGUAGES = {
  en: "English",
  ja: "日本語",
  "zh-TW": "繁體中文",
} as const

const UserNav = ({
  className,
  user,
  showProfile = false,
  size = "md",
}: UserNavProps) => {
  const { t, i18n } = useTranslation() // Add i18n
  const navigate = useNavigate()
  const setUserMe = useStore((state) => state.setUserMe)
  const { fetchFn: logout } = useLogoutApi()
  const avatarUrl = useResourceUrl(user?.avatar)

  const handleLanguageChange = (lang: keyof typeof LANGUAGES) => {
    if (i18n.language === lang) return // Don't reload if it's the same language
    i18n.changeLanguage(lang)
  }

  const handleLogout = () => {
    localStorage.removeItem("loggedIn")
    localStorage.removeItem("redirectAfterLogin")
    logout({
      callbackFn: (resp) => {
        if (!resp) return
        console.log("logged out", resp)

        // wait for some components (e.g. sidebar) to unmount to avoid making the login form freezed
        setTimeout(() => {
          setUserMe(null)
          navigate("/login")
        }, 200)
      },
    })
  }

  if (!user) {
    return (
      <Button variant="outline" onClick={() => navigate("/login")}>
        {t("common.userNav.login")}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "relative rounded-xl focus-visible:ring-0 focus-visible:ring-offset-0",
            className
          )}
        >
          <Avatar
            className={cn(
              "w-10 h-10",
              size === "sm" && "w-8 h-8",
              size === "lg" && "w-12 h-12"
            )}
          >
            <AvatarImage
              src={avatarUrl}
              alt={user.name ?? ""}
              className="object-cover"
            />
            <AvatarFallback
              style={{
                backgroundColor: getAvatarBackgroundColor(user.email),
              }}
            >
              {getAvatarName(user.name?.[0])}
            </AvatarFallback>
          </Avatar>
          {showProfile && (
            <div className="flex flex-col ml-1 items-start">
              <div className="text-base">{ellipsis(user.name, 16)}</div>
              <div className="text-sm text-muted-foreground">
                {ellipsis(user.email, 16)}
              </div>
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => navigate(SETTINGS_ENDPOINTS.accountProfile)}
          >
            {t("common.userNav.profile.title")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate(SETTINGS_ENDPOINTS.accountMetrics)}
          >
            {t("common.userNav.usage.title")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages className="w-4 h-4 mr-2" />
              <span>{t("common.userNav.language.title")}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <DropdownMenuCheckboxItem
                    key={code}
                    checked={i18n.language === code}
                    onClick={() =>
                      handleLanguageChange(code as keyof typeof LANGUAGES)
                    }
                  >
                    <span className="flex-1">{name}</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          {t("common.userNav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserNav
